import { useEffect, useState } from "react";
import type { Stripe } from "@stripe/stripe-js";
import { captureException } from "@sentry/nextjs";
import { getStripe } from "./getStripe";
import { IUseLoadStripeRes } from "./types";

export function useLoadStripe(
  stripeKey: string | undefined,
): IUseLoadStripeRes {
  const [loading, setLoading] = useState(true);
  const [stripe, setStripe] = useState<Stripe | null>(null);

  useEffect(() => {
    // Do not set state after unmount.
    let cancelled = false;

    async function loadStripeAsync(publishableKey: string): Promise<void> {
      try {
        const loadedStripe = await getStripe(publishableKey);
        if (!cancelled) {
          setStripe(loadedStripe);
        }
      } catch {
        // getStripe reports the error.
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    // Show the load error, not a page crash, when the build has no key.
    if (!stripeKey) {
      captureException(new Error("`NEXT_PUBLIC_STRIPE_KEY` is missing"));
      setLoading(false);
    } else {
      loadStripeAsync(stripeKey);
    }

    return () => {
      cancelled = true;
    };
  }, [stripeKey]);

  return { loading, stripe };
}
